import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button, Callout, Modal, Spinner, Tabs } from '@/components/ui';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchAccount } from '@/api/accounts';
import type { ScopedAccountRow } from '@/api/orgAdmin';
import { AccountAuditPanel } from '@/pages/Console/Audit/AccountAuditPanel';
import { useProvisioningSwitch } from '@/pages/Console/useProvisioningSwitch';
import shared from './ScopedAccounts.module.css';
import styles from './ScopedAccountProfileModal.module.css';

type ProfileTab = 'overview' | 'levels' | 'audit';

function formatDate(value: string | number | null | undefined, fallback: string): string {
    if (!value) return fallback;
    const date = typeof value === 'number' ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) return fallback;
    return date.toLocaleString('vi-VN');
}

/**
 * EAA-1817: what to print on the single "Cấp phát" line, given that the row behind
 * it covers SEVERAL applications.
 *
 * `sync_status` carries the worst outcome across every app, so a grant that reached
 * ACH and failed on LMS reads a bare "error" — and the reporter twice concluded
 * that the broken app had blocked the working one. It had not: the apps are pushed
 * independently. When they disagree, say so ("một phần") and let the per-app rows
 * below carry the detail; a partial result is a different fact from a total
 * failure and deserves a different word.
 */
function aggregateStatus(
    row: ScopedAccountRow,
    provisioningOff: boolean,
    t: TFunction
): React.ReactNode {
    const base = 'console.hierarchy.scopedAccounts.profile.';
    // "pending" alone is a lie of omission when the master switch is off — nothing
    // is queued, every push was refused and the refusal swallowed, so the row would
    // read "pending" forever. Say which it is.
    if (provisioningOff && row.sync_status === 'pending') {
        return <span className={shared.warnText}>{t(`${base}syncStatusBlocked`)}</span>;
    }
    const detail = row.last_outcome_detail;
    if (detail) {
        const codes = Object.keys(detail);
        const applied = codes.filter((c) => {
            const o = detail[c].outcome;
            return o === 'pushed' || o === 'revoked';
        });
        if (applied.length > 0 && applied.length < codes.length) {
            const failing = codes.filter((c) => !applied.includes(c));
            return (
                <span className={shared.warnText}>
                    {t(`${base}syncStatusPartial`, {
                        ok: applied.map((c) => c.toUpperCase()).join(', '),
                        failed: failing.map((c) => c.toUpperCase()).join(', '),
                    })}
                </span>
            );
        }
    }
    return row.sync_status;
}

/**
 * EAA-1817: turn a provisioning outcome into a sentence an operator can act on.
 *
 * The raw `detail` is a Python exception — the reporter's screen showed
 * "Provisioning failed (0): HTTPConnectionPool(host='lms-api', port=8000): Max
 * retries exceeded with url: /api/v1/scim/v2/Users (Caused by
 * NameResolutionError(...))" in red, twice. That text tells an operator nothing
 * they can do and buries the one fact that matters, which is that the OTHER app
 * was provisioned fine.
 *
 * The cause is recognised from the raw string rather than from a structured code
 * because the server does not send one: `detail` is `str(exc)`. Matching on
 * substrings is fragile by nature, so an unrecognised detail falls back to a
 * generic sentence plus the raw text — never to silence, which would hide a real
 * failure the moment a message is reworded upstream.
 */
function friendlyOutcome(
    outcome: string,
    detail: string | undefined,
    t: TFunction
): { text: string; failed: boolean; raw?: string } {
    const base = 'console.hierarchy.scopedAccounts.profile.outcome.';
    if (outcome === 'pushed') return { text: t(`${base}pushed`), failed: false };
    if (outcome === 'revoked') return { text: t(`${base}revoked`), failed: false };
    if (outcome === 'no_mapping') return { text: t(`${base}noMapping`), failed: true };
    if (outcome === 'paused_mapping') return { text: t(`${base}paused`), failed: true };
    if (outcome === 'ambiguous_mapping') return { text: t(`${base}ambiguous`), failed: true };
    if (outcome === 'pending_revoke') return { text: t(`${base}pendingRevoke`), failed: true };
    if (outcome === 'drift') return { text: t(`${base}drift`), failed: true, raw: detail };
    if (outcome === 'error') {
        const raw = detail ?? '';
        const unreachable =
            raw.includes('NameResolutionError') ||
            raw.includes('Max retries exceeded') ||
            raw.includes('ConnectionError') ||
            raw.includes('Connection refused') ||
            raw.includes('timed out');
        return {
            text: t(unreachable ? `${base}unreachable` : `${base}error`),
            failed: true,
            raw,
        };
    }
    return { text: outcome, failed: false };
}

/**
 * EAA-1817: the per-application provisioning result, one row each.
 *
 * Ordered by app code so the list does not reshuffle between opens. Each row says
 * what happened to THAT application in plain language; the raw server text stays
 * on the `title` attribute so it is one hover away when someone needs to debug,
 * without being the first thing an operator reads.
 */
function perAppRows(
    row: ScopedAccountRow,
    t: TFunction
): [string, React.ReactNode][] {
    const detail = row.last_outcome_detail;
    if (!detail) return [];
    return Object.keys(detail)
        .sort()
        .map((appCode) => {
            const entry = detail[appCode];
            const friendly = friendlyOutcome(entry.outcome, entry.detail, t);
            const label = t('console.hierarchy.scopedAccounts.profile.perApp', {
                app: appCode.toUpperCase(),
            });
            const value = (
                <span
                    key={appCode}
                    className={friendly.failed ? shared.warnText : undefined}
                    title={friendly.raw || undefined}
                >
                    {friendly.text}
                </span>
            );
            return [label, value] as [string, React.ReactNode];
        });
}

export function ScopedAccountProfileModal({
    row,
    onClose,
    onAssignLevel,
    onReset,
    onTransfer,
    onRevoke,
    canManage,
    canReset,
    orgName,
}: {
    row: ScopedAccountRow | null;
    onClose: () => void;
    onAssignLevel: (row: ScopedAccountRow) => void;
    onReset: (row: ScopedAccountRow) => void;
    onTransfer: (row: ScopedAccountRow) => void;
    onRevoke: (row: ScopedAccountRow) => void;
    canManage: boolean;
    canReset: boolean;
    orgName: string | null;
}) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const can = useCan();
    const provisioning = useProvisioningSwitch();
    const [tab, setTab] = useState<ProfileTab>('overview');

    const canReadIdentity = can('console.accounts.view');
    const detailQuery = useQuery({
        queryKey: ['account-detail', accessToken, row?.user_sub],
        queryFn: () => fetchAccount(config.apiBase, accessToken as string, row?.user_sub as string),
        enabled: Boolean(row && accessToken && canReadIdentity),
        retry: false,
    });

    if (!row) return null;

    const detail = detailQuery.data;
    const dash = t('console.hierarchy.scopedAccounts.profile.unknown');
    // EAA-1839 (2026-08-24, user report: the modal title showed a raw sub UUID):
    // the title used `row.user_email || row.user_sub` — the GRANT's own fields,
    // which is blank whenever `Assignment.user_email` is (see `emailBySub`'s
    // docstring in `useScopedAccountUsernames.ts` for why that snapshot can be
    // empty). `detail.username` is the identity fetch this modal already makes
    // (`GET /accounts/{sub}`, gated on `canReadIdentity`) — the same field the
    // "ĐỊNH DANH" block below already prints as "Username" — so preferring it
    // matches the table's own identity precedence (username, then email, then a
    // truncated sub) instead of falling straight to the sub whenever the
    // grant's own denormalized email happens to be blank. Falls back to
    // `row.user_email` while the identity fetch is still loading/unavailable,
    // and only then to a truncated sub — never the raw 36-character UUID.

    const identityRows: [string, React.ReactNode][] = canReadIdentity
        ? [
              [t('console.hierarchy.scopedAccounts.profile.username'), detail?.username ?? dash],
              [
                  t('console.hierarchy.scopedAccounts.profile.email'),
                  detail?.email || row.user_email || dash,
              ],
              [t('console.hierarchy.scopedAccounts.profile.sub'), <code key="sub">{row.user_sub}</code>],
              [
                  t('console.hierarchy.scopedAccounts.profile.enabled'),
                  detail
                      ? t(
                            detail.enabled
                                ? 'console.hierarchy.scopedAccounts.profile.enabledYes'
                                : 'console.hierarchy.scopedAccounts.profile.enabledNo'
                        )
                      : dash,
              ],
              [
                  t('console.hierarchy.scopedAccounts.profile.requiredActions'),
                  detail?.required_actions?.length
                      ? detail.required_actions.join(', ')
                      : t('console.hierarchy.scopedAccounts.profile.requiredActionsNone'),
              ],
              [
                  t('console.hierarchy.scopedAccounts.profile.created'),
                  formatDate(detail?.created ?? null, dash),
              ],
          ]
        : [
              [
                  t('console.hierarchy.scopedAccounts.profile.email'),
                  row.user_email || dash,
              ],
              [t('console.hierarchy.scopedAccounts.profile.sub'), <code key="sub">{row.user_sub}</code>],
          ];

    const orgRows: [string, React.ReactNode][] = [
        [t('console.hierarchy.scopedAccounts.profile.org'), orgName ?? row.organization_id],
        [
            t('console.hierarchy.scopedAccounts.profile.level'),
            row.level_name ?? (
                <span className={shared.warnText}>
                    {t('console.hierarchy.scopedAccounts.table.noLevel')}
                </span>
            ),
        ],
        [t('console.hierarchy.scopedAccounts.profile.roleKey'), row.role_key ?? dash],
        [
            t('console.hierarchy.scopedAccounts.profile.grantState'),
            t(
                row.is_active
                    ? 'console.hierarchy.scopedAccounts.table.statusOk'
                    : 'console.hierarchy.scopedAccounts.table.statusRevoked'
            ),
        ],
        [t('console.hierarchy.scopedAccounts.profile.grantedAt'), formatDate(row.granted_at, dash)],
        [t('console.hierarchy.scopedAccounts.profile.grantedBy'), row.granted_by || dash],
        [
            t('console.hierarchy.scopedAccounts.profile.syncStatus'),
            // EAA-1817: "pending" alone is a lie of omission when the master switch
            // is off — nothing is queued, every push was refused and the refusal
            // swallowed, so this row would read "pending" forever. Say which it is.
            aggregateStatus(row, provisioning.off, t),
        ],
        [
            t('console.hierarchy.scopedAccounts.profile.lastSyncedAt'),
            formatDate(row.last_synced_at, dash),
        ],
        // EAA-1817: one line PER APPLICATION. `sync_status` above is the worst
        // outcome across every app this org provisions into, so a grant that reached
        // ACH and failed on LMS reads "error" — and the operator concludes the whole
        // assignment failed and that a broken app blocks the working one. It does
        // not: `provisioning.apply` pushes each target independently. This row is
        // what makes that visible instead of only true.
        ...perAppRows(row, t),
        // EAA-1817: the raw `last_sync_error` blob is NOT shown when the per-app rows
        // above already explain each application. It is the same Python exception
        // repeated a second time in red, and it was the loudest thing in the dialog
        // while saying the least. It survives only for rows too old to carry
        // `last_outcome_detail`, where it is the only account of what happened.
        ...(row.last_sync_error && !row.last_outcome_detail
            ? ([
                  [
                      t('console.hierarchy.scopedAccounts.profile.lastSyncError'),
                      <span key="err" className={shared.warnText}>
                          {row.last_sync_error}
                      </span>,
                  ],
              ] as [string, React.ReactNode][])
            : []),
    ];

    /**
     * EAA-1839: the grid is two columns (see the module's `.dl`), so a row whose
     * value is genuinely long — a 36-char sub, a full Level name, a provisioning
     * error — must be able to claim the full width instead of wrapping three times
     * inside half of it. `WIDE_ROW_LABELS` names those by their own label rather
     * than by index, so reordering the rows cannot silently move the exemption to
     * the wrong one.
     */
    const WIDE_ROW_LABELS = new Set([
        t('console.hierarchy.scopedAccounts.profile.sub'),
        t('console.hierarchy.scopedAccounts.profile.level'),
        t('console.hierarchy.scopedAccounts.profile.lastSyncError'),
    ]);

    function definitionList(entries: [string, React.ReactNode][], heading: string) {
        return (
            <section className={styles.block}>
                <h4 className={styles.blockTitle}>{heading}</h4>
                <dl className={styles.dl}>
                    {entries.map(([label, value]) => (
                        <div
                            key={label}
                            className={
                                styles.dlRow +
                                (WIDE_ROW_LABELS.has(label) ? ` ${styles.dlRowWide}` : '')
                            }
                        >
                            <dt className={styles.dlTerm}>{label}</dt>
                            <dd className={styles.dlValue}>{value}</dd>
                        </div>
                    ))}
                </dl>
            </section>
        );
    }

    let panel: React.ReactNode = null;
    if (tab === 'overview') {
        panel = (
            <>
                {!row.level_name && (
                    <Callout tone="warning" longLabel>
                        {t('console.hierarchy.scopedAccounts.profile.noLevelWarning')}
                        {canManage && (
                            <Button
                                className={styles.inlineCta}
                                type="primary"
                                size="small"
                                onClick={() => onAssignLevel(row)}
                            >
                                {t('console.hierarchy.scopedAccounts.profile.assignLevelNow')}
                            </Button>
                        )}
                    </Callout>
                )}
                {detailQuery.isLoading && <Spinner />}
                {!canReadIdentity && (
                    <Callout tone="info" longLabel>
                        {t('console.hierarchy.scopedAccounts.profile.identityNoPermission')}
                    </Callout>
                )}
                {detailQuery.isError && canReadIdentity && (
                    <Callout tone="danger" longLabel>
                        {t('console.hierarchy.scopedAccounts.profile.identityLoadFailed')}
                    </Callout>
                )}
                {definitionList(identityRows, t('console.hierarchy.scopedAccounts.profile.identityHeading'))}
                {definitionList(orgRows, t('console.hierarchy.scopedAccounts.profile.orgHeading'))}
            </>
        );
    }

    if (tab === 'levels') {
        panel = !canReadIdentity ? (
            <Callout tone="info" longLabel>
                {t('console.hierarchy.scopedAccounts.profile.identityNoPermission')}
            </Callout>
        ) : (
            <>
                <section className={styles.block}>
                    <h4 className={styles.blockTitle}>
                        {t('console.hierarchy.scopedAccounts.profile.placementsHeading')}
                    </h4>
                    <ul className={styles.list}>
                        {(detail?.placements ?? []).map((placement) => (
                            <li
                                key={`${placement.organization_id}:${placement.level_name}`}
                                className={styles.listItem}
                            >
                                <strong>{placement.organization_name}</strong>
                                <span>{placement.level_name}</span>
                                <code>{placement.role_key}</code>
                            </li>
                        ))}
                        {detail && detail.placements.length === 0 && (
                            <li className={`${styles.listItem} ${styles.listEmpty}`}>
                                {t('console.hierarchy.scopedAccounts.profile.placementsEmpty')}
                            </li>
                        )}
                    </ul>
                </section>
                <section className={styles.block}>
                    <h4 className={styles.blockTitle}>
                        {t('console.hierarchy.scopedAccounts.profile.appAccessHeading')}
                    </h4>
                    <ul className={styles.list}>
                        {(detail?.app_access ?? []).map((app) => (
                            <li key={app.application} className={styles.listItem}>
                                <strong>{app.application_name || app.application}</strong>
                                <code>{app.roles.join(', ') || '—'}</code>
                            </li>
                        ))}
                        {detail && detail.app_access.length === 0 && (
                            <li className={`${styles.listItem} ${styles.listEmpty}`}>
                                {t('console.hierarchy.scopedAccounts.profile.appAccessEmpty')}
                            </li>
                        )}
                    </ul>
                </section>
            </>
        );
    }

    if (tab === 'audit') panel = <AccountAuditPanel />;

    return (
        <Modal
            open
            onCancel={onClose}
            // `width` matches this screen's other two modals' family (720 for the
            // import wizard's mapping table); 640 is enough for the definition
            // list's label/value pair without stretching short values across the
            // dialog. The drawer's old 480 was a side-panel width.
            // EAA-1839 (2026-08-21, user request): 960, up from 640. At 640 the
            // body scrolled for a list of only ~14 short rows while leaving a wide
            // empty gutter beside every value — the dialog was tall and narrow for
            // content that is neither. The extra width pays for the two-column
            // definition grid in `20-scoped-accounts.css`, which is what actually
            // removes the scrolling; widening alone would not have.
            width={960}
            className="sa-profile"
            title={
                detail?.username ||
                row.user_email ||
                (row.user_sub ? `${row.user_sub.slice(0, 8)}…` : dash)
            }
            footer={
                <div className={styles.footer}>
                    {canReset && (
                        <Button onClick={() => onReset(row)}>
                            {t('console.hierarchy.scopedAccounts.table.actionResetPassword')}
                        </Button>
                    )}
                    {canManage && row.is_active && (
                        <Button onClick={() => onTransfer(row)}>
                            {t('console.hierarchy.scopedAccounts.table.actionTransfer')}
                        </Button>
                    )}
                    {canManage && row.is_active && (
                        <Button danger onClick={() => onRevoke(row)}>
                            {t('console.hierarchy.scopedAccounts.table.actionRevoke')}
                        </Button>
                    )}
                </div>
            }
        >
            <Tabs<ProfileTab>
                className={styles.tabs}
                activeKey={tab}
                onChange={setTab}
                items={[
                    {
                        key: 'overview',
                        label: t('console.hierarchy.scopedAccounts.profile.tabOverview'),
                    },
                    {
                        key: 'levels',
                        label: t('console.hierarchy.scopedAccounts.profile.tabLevels'),
                    },
                    { key: 'audit', label: t('console.hierarchy.scopedAccounts.profile.tabAudit') },
                ]}
            />
            <div className={styles.panel}>{panel}</div>
        </Modal>
    );
}

export default ScopedAccountProfileModal;
